import React, {useState} from 'react';
import LabelAndInput from './UI/LabelAndInput';
import Button from './UI/Button';
import {Context} from '../context';
import ErrorNote from './UI/ErrorNote';
import Section from './UI/Section';
import {
  getPasswordErrors,
  getInputValueByName,
  shouldShowField
} from '../helperFunctions';
import {useNavigate} from 'react-router-dom';
import Recaptcha from './UI/Recaptcha';
import AuthComPromInterface from './UI/AuthComProm';
import {registrationFields as regFields} from '../settings.js';

function Register(props) {
  const navigate = useNavigate();
  const inputWidth = 'w-80';

  const [termsAndConditions, setTermsAndConditions] = useState(false);
  const [errors, setErrors] = useState([]);

  const {registerAccount, updateAddress, localeText, loading, loginUser, showError} =
    React.useContext(Context);

  const {emailOrMail, AuthComProm} = AuthComPromInterface({localeText});

  const submitHandler = async e => {
    e.preventDefault();

    const firstName = getInputValueByName(e, 'firstName');
    const lastName = getInputValueByName(e, 'lastName');
    const email = getInputValueByName(e, 'email');
    const emailConfirmation = getInputValueByName(e, 'confirm-email');
    const phone = getInputValueByName(e, 'phone');
    const password = getInputValueByName(e, 'password');
    const passwordConfirmation = getInputValueByName(e, 'confirm-password');
    const addressLine1 = getInputValueByName(e, 'addressLine1');
    const addressLine2 = getInputValueByName(e, 'addressLine2');
    const city = getInputValueByName(e, 'city');
    const state = getInputValueByName(e, 'state');
    const postalCode = getInputValueByName(e, 'postalCode');

    const currentErrors = {};

    if (!window.recaptchaPassed && !window.Cypress)
      currentErrors.recaptcha = localeText(
        'rw_register_msg_recaptcha_incomplete',
        'Please complete the reCAPTCHA'
      );

    if (!termsAndConditions)
      currentErrors.termsAndConditions = localeText(
        'rw_register_msg_please_accept_terms',
        'Please review and accept Terms and Conditions to proceed.'
      );

    if (email !== emailConfirmation)
      currentErrors.emailError = localeText(
        'rw_register_msg_emails_do_not_match',
        'Emails do not match.'
      );

    if (phone && phone.toString().length !== 10)
      currentErrors.phoneError = localeText(
        'rw_register_msg_mobile_wrong_length',
        'Phone number must be 10 digits'
      );

    // password checks
    const passwordErrors = getPasswordErrors(
      password,
      passwordConfirmation,
      localeText
    );
    if (passwordErrors.length > 0)
      currentErrors.passwordErrors = passwordErrors;
    if (Object.keys(currentErrors).length > 0) {
      setErrors(currentErrors);
      return;
    }

    const registerPayload = {
      username: email,
      email: email,
      password: password,
      communicationPreference: emailOrMail
    };

    if (shouldShowField(regFields.name)) {
      registerPayload.firstName = firstName ? firstName : 'Anonymous';
      registerPayload.lastName = lastName ? lastName : 'Anonymous';
    }

    if (shouldShowField(regFields.mobile)) {
      registerPayload.phone = phone ? phone : '0000000000';
    }

    const regAccRes = await registerAccount(registerPayload);

    if (regAccRes.errors) {
      currentErrors.registerError = regAccRes.errors.map(err => err.error.slug);
      setErrors(currentErrors);
      return;
    }

    const addressPayload = {
      userId: regAccRes?.profile?.userId,
      country: 'United States'
    };

    if (shouldShowField(regFields.address)) {
      addressPayload.addressLine1 = addressLine1;
      addressPayload.addressLine2 = addressLine2;
    }

    if (shouldShowField(regFields.city)) {
      addressPayload.city = city;
    }

    if (shouldShowField(regFields.state)) {
      addressPayload.state = state;
    }

    if (shouldShowField(regFields.zipCode)) {
      addressPayload.postalCode = postalCode;
    }

    await updateAddress(addressPayload);

    const loginUserRes = await loginUser({
      username: email,
      password
    });

    if (loginUserRes.errors) {
      const isUnverifiedAccount = loginUserRes.errors.some(
        err => err.error?.slug === 'serr_denied-email-unverified'
      );

      if (isUnverifiedAccount) {
        navigate('/register/verification', {state: {email, verificationEmailSent: true}});
        return;
      }

      showError(
        'rw_register_msg_verify_email',
        'Account created! Please check your email to verify your account before logging in.'
      );
      return;
    }

    navigate('/loadfunds');
  };

  return (
    <Section
      sectionId='register'
      heading={localeText('rw_register_header', 'Register')}
      className={props.className}
    >
      <form onSubmit={submitHandler}>
        {shouldShowField(regFields.name) && (
          <div className='flex flex-col sm:flex-row sm:gap-4'>
            <LabelAndInput
              name='firstName'
              label={localeText('rw_register_title_first_name', 'First Name')}
              type='text'
              placeholder={localeText(
                'rw_register_title_first_name_placeholder',
                'First Name'
              )}
              className={inputWidth}
              required={regFields.name === 'required'}
              input_testid='register_input_first_name'
              label_testid='myaccount_label_first_name'
            />
            <LabelAndInput
              name='lastName'
              label={localeText('rw_register_title_last_name', 'Last Name')}
              type='text'
              placeholder={localeText(
                'rw_register_title_last_name_placeholder',
                'Last Name'
              )}
              className={inputWidth}
              required={regFields.name === 'required'}
              input_testid='register_input_last_name'
              label_testid='register_label_last_name'
            />
          </div>
        )}

        <div className='flex flex-col sm:flex-row sm:gap-4'>
          <LabelAndInput
            name='email'
            label={localeText('rw_register_title_email', 'Email')}
            type='email'
            placeholder={localeText(
              'rw_register_title_email_placeholder',
              'Email'
            )}
            className={inputWidth}
            required
            input_testid='register_input_email'
            label_testid='register_label_email'
          />
          <LabelAndInput
            name='confirm-email'
            label={localeText(
              'rw_register_title_email_confirm',
              'Confirm Email'
            )}
            type='email'
            placeholder={localeText(
              'rw_register_title_email_confirm_placeholder',
              'Retype Email'
            )}
            className={inputWidth}
            required
            input_testid='register_input_confirm_email'
            label_testid='register_label_confirm_email'
          />
        </div>
        {errors && errors.emailError && (
          <ErrorNote>{errors.emailError}</ErrorNote>
        )}

        {shouldShowField(regFields.mobile) && (
          <>
            <div className='flex flex-col sm:flex-row sm:gap-4'>
              <LabelAndInput
                name='phone'
                label={localeText('rw_register_title_mobile', 'Mobile')}
                placeholder={localeText(
                  'rw_register_title_mobile_placeholder',
                  '10-Digit Number'
                )}
                type='number'
                className={inputWidth}
                required={regFields.mobile === 'required'}
                input_testid='register_input_phone'
                label_testid='register_label_phone'
              />
              <LabelAndInput
                name='filler'
                label='filler'
                type='text'
                className={`${inputWidth} invisible hidden sm:block`}
              />
            </div>
            {errors && errors.phoneError && (
              <ErrorNote>{errors.phoneError}</ErrorNote>
            )}
          </>
        )}
        {shouldShowField(regFields.address) && (
          <div className='flex flex-col sm:flex-row sm:gap-4'>
            <LabelAndInput
              name='addressLine1'
              label={localeText(
                'rw_register_title_address_line_1',
                'Address Line 1'
              )}
              type='text'
              placeholder={localeText(
                'rw_register_title_address_1_placeholder',
                'Address Line 1'
              )}
              className={inputWidth}
              required={regFields.address === 'required'}
              input_testid='register_input_address_line_1'
              label_testid='register_label_address_line_1'
            />
            <LabelAndInput
              name='addressLine2'
              label={localeText(
                'rw_register_title_address_line_2',
                'Address Line 2'
              )}
              type='text'
              placeholder={localeText(
                'rw_register_title_address_2_placeholder',
                'Address Line 2'
              )}
              className={inputWidth}
              input_testid='register_input_address_line_2'
              label_testid='register_label_address_line_2'
            />
          </div>
        )}
        {(shouldShowField(regFields.city) ||
          shouldShowField(regFields.state)) && (
          <div className='flex flex-col sm:flex-row sm:gap-4'>
            {shouldShowField(regFields.city) && (
              <LabelAndInput
                name='city'
                label={localeText('rw_register_title_city', 'City')}
                type='text'
                placeholder={localeText(
                  'rw_register_title_city_placeholder',
                  'City'
                )}
                className={inputWidth}
                required={regFields.city === 'required'}
                input_testid='register_input_city'
                label_testid='register_label_city'
              />
            )}
            {shouldShowField(regFields.state) && (
              <LabelAndInput
                name='state'
                label={localeText('rw_register_title_state', 'State')}
                type='text'
                placeholder={localeText(
                  'rw_register_title_state_placeholder',
                  'State'
                )}
                className={inputWidth}
                required={regFields.state === 'required'}
                input_testid='register_input_state'
                label_testid='register_label_state'
              />
            )}
          </div>
        )}
        {shouldShowField(regFields.zipCode) && (
          <div className='flex flex-col sm:flex-row sm:gap-4'>
            <LabelAndInput
              name='postalCode'
              label={localeText('rw_register_title_zip_code', 'Zip Code')}
              placeholder={localeText(
                'rw_register_title_zip_code_placeholder',
                'Zip Code'
              )}
              type='number'
              className={inputWidth}
              required={regFields.zipCode === 'required'}
              input_testid='register_input_postal_code'
              label_testid='register_label_postal_code'
            />
            <LabelAndInput
              name='filler'
              label='filler'
              type='text'
              className={`${inputWidth} invisible hidden sm:block`}
            />
          </div>
        )}
        <div className='flex flex-col sm:flex-row sm:gap-4'>
          <LabelAndInput
            name='password'
            label={localeText('rw_register_title_password', 'Password')}
            type='password'
            placeholder={localeText(
              'rw_register_title_password_placeholder',
              'Password'
            )}
            className={inputWidth}
            required
            input_testid='register_input_password'
            label_testid='register_label_password'
          />
          <LabelAndInput
            name='confirm-password'
            label={localeText(
              'rw_register_title_password_confirm',
              'Confirm Password'
            )}
            type='password'
            placeholder={localeText(
              'rw_register_title_password_confirm_placeholder',
              'Retype Password'
            )}
            className={inputWidth}
            required
            input_testid='register_input_confirm_password'
            label_testid='register_label_confirm_password'
          />
        </div>
        {errors &&
          errors.passwordErrors &&
          errors.passwordErrors.map((error, i) => (
            <ErrorNote key={i}>{error}</ErrorNote>
          ))}
        <AuthComProm />
        <div className='mt-4'>
          <input
            name='terms-and-conditions'
            title='terms-and-conditions'
            type='checkbox'
            value={termsAndConditions}
            onClick={() => setTermsAndConditions(prev => !prev)}
            id='terms-and-conditions'
            data-testid='register_input_terms_and_conditions'
          />
          <label
            htmlFor='terms-and-conditions'
            className='ml-4'
            data-testid='register_label_terms_and_conditions'
          >
            {localeText(
              'rw_register_msg_terms_agree',
              'I agree to the Terms and Conditions.'
            )}
          </label>
        </div>
        {errors && errors.termsAndConditions && (
          <ErrorNote>{errors.termsAndConditions}</ErrorNote>
        )}
        <Recaptcha />
        {errors && errors.recaptcha && (
          <ErrorNote>
            {localeText(
              'rw_register_msg_recaptcha_incomplete',
              'Please complete the reCAPTCHA'
            )}
          </ErrorNote>
        )}
        <Button
          className='mt-4'
          loading={loading}
          loadingtext={localeText(
            'rw_register_button_creating_acc',
            'Creating Account'
          )}
          type='submit'
          data-testid='register_submit_button_register'
        >
          {localeText('rw_register_button_create_acc', 'Create Account')}
        </Button>
        {errors && errors.registerError && (
          <ErrorNote className='pt-4'>
            {localeText(
              errors.registerError,
              'Registration Failed: ' + errors.registerError
            )}
          </ErrorNote>
        )}
      </form>
    </Section>
  );
}

export default Register;
